import { api } from './apiClient';

export type AgendaEventType = 'APPOINTMENT' | 'COMPROMISE' | 'BLOCKING';

/** Evento da agenda interna persistido na API (atendimento, compromisso ou bloqueio). */
export type AgendaEventResponse = {
  id: string;
  professionalId: string;
  type: AgendaEventType;
  title: string;
  date: string;
  start: string;
  end: string;
  allDay: boolean;
  patientId?: string | null;
  patientName?: string | null;
  modality?: string | null;
  locationName?: string | null;
  notes?: string | null;
  serviceName?: string | null;
  serviceValue?: string | null;
  recurrence?: string | null;
  recurrenceCount?: number | null;
  notifyPatient?: boolean | null;
  /** Resposta do paciente pelos links do WhatsApp (Confirmar/Cancelar). */
  patientResponse?: 'PENDENTE' | 'CONFIRMADO' | 'CANCELADO' | null;
  patientRespondedAt?: string | null;
  attendance?: string | null;
  /** Bloco #56: sala do Google Meet criada para esta teleconsulta. */
  meetLink?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type AgendaEventPayload = {
  type: AgendaEventType;
  title: string;
  date: string;
  start: string;
  end: string;
  allDay?: boolean;
  professionalId?: string;
  patientId?: string;
  patientName?: string;
  modality?: string;
  locationName?: string;
  notes?: string;
  serviceName?: string;
  serviceValue?: string;
  recurrence?: string;
  recurrenceCount?: number;
  notifyPatient?: boolean;
  attendance?: string;
};

export function fetchAgendaEvents(from?: string, to?: string): Promise<AgendaEventResponse[]> {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const query = params.toString();
  return api.get<AgendaEventResponse[]>(`/agenda/events${query ? `?${query}` : ''}`);
}

/** Cria um ou vários eventos (recorrência e período geram vários de uma vez). */
export function createAgendaEvents(events: AgendaEventPayload[]): Promise<AgendaEventResponse[]> {
  return api.post<AgendaEventResponse[]>('/agenda/events', { events });
}

export function updateAgendaEvent(
  id: string,
  payload: Partial<AgendaEventPayload>,
): Promise<AgendaEventResponse> {
  return api.patch<AgendaEventResponse>(`/agenda/events/${encodeURIComponent(id)}`, payload);
}

/**
 * `notifyPatient`: bloco #53 — o paciente recebe o aviso de cancelamento por WhatsApp.
 * `scope: 'series'`: bloco #73 — cancela esta ocorrência e as seguintes da recorrência.
 */
export function deleteAgendaEvent(
  id: string,
  notifyPatient = false,
  scope: 'one' | 'series' = 'one',
): Promise<{ success: boolean; removedIds?: string[] }> {
  const params = new URLSearchParams();
  if (notifyPatient) params.set('notifyPatient', 'true');
  if (scope === 'series') params.set('scope', 'series');
  const query = params.toString();
  return api.delete(`/agenda/events/${encodeURIComponent(id)}${query ? `?${query}` : ''}`);
}

/** Bloco #57: resultado do "Acessar Sala" por canal (nunca impede o acesso). */
export type TeleconsultaChannelResult = 'ENVIADO' | 'JA_ENVIADO' | 'SEM_CONTATO' | 'DESLIGADO' | 'NAO_CONFIGURADO' | 'FALHOU';
export type TeleconsultaAccessResult = {
  roomUrl: string | null;
  reason?: 'PRESENCIAL' | 'SEM_SALA';
  whatsapp?: TeleconsultaChannelResult;
  email?: TeleconsultaChannelResult;
};

/**
 * "Acessar Sala": a API envia o link da sala ao paciente (WhatsApp e e-mail)
 * e devolve esse link. `kind`: `agenda` (agenda interna) ou `appointment`
 * (reserva do link externo).
 */
export function accessTeleconsulta(kind: 'agenda' | 'appointment', id: string): Promise<TeleconsultaAccessResult> {
  return api.post<TeleconsultaAccessResult>(`/teleconsulta/${kind}/${encodeURIComponent(id)}/acessar`);
}
